import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    BookOpen,
    Users,
    DollarSign,
    TrendingUp,
    Award,
    FileText,
    UserCheck,
    Clock,
    CheckCircle,
    MapPin,
    User,
    Download
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export default function CourseDetailsPage() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [courseData, setCourseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedYear, setSelectedYear] = useState('');

    useEffect(() => {
        fetchCourseDetails();
    }, [courseId]);

    useEffect(() => {
        if (courseData) {
            const years = getAcademicYears();
            if (years.length > 0 && !selectedYear) {
                setSelectedYear(years[0]);
            }
        }
    }, [courseData]);

    const getAcademicYears = () => {
        if (!courseData) return [];
        const studentYears = courseData.students.map(s => s.academicYear);
        const paymentYears = (courseData.payments.allTransactions || []).map(p => p.academicYear);
        const allYears = [...new Set([...studentYears, ...paymentYears])];
        return allYears.sort((a, b) => b.localeCompare(a));
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        const yearSuffix = selectedYear ? `_AY_${selectedYear}` : '';

        // Header
        doc.setFontSize(20);
        doc.setTextColor(40, 40, 40);
        doc.text(courseData.course.name, 14, 22);

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Course Report ${selectedYear ? `(AY ${selectedYear})` : ''}`, 14, 28);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 33);
        doc.text(`Campus: ${courseData.course.campusName}`, 14, 38);

        // Metrics Section
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text("Overview", 14, 50);

        const metricsData = [
            ['Total Students', courseData.metrics.totalStudents],
            ['Total Units', courseData.metrics.totalUnits],
            ['Avg Grade', `${courseData.metrics.averageGrade}%`],
            ['Attendance', `${courseData.metrics.attendanceRate}%`],
            ['Completion Rate', `${courseData.metrics.assignmentCompletionRate}%`]
        ];

        autoTable(doc, {
            startY: 55,
            head: [['Metric', 'Value']],
            body: metricsData,
            theme: 'grid',
            headStyles: { fillColor: [79, 70, 229], textColor: 255 },
            styles: { fontSize: 10, cellPadding: 5 }
        });

        // Students Section
        let finalY = doc.lastAutoTable.finalY + 15;
        doc.text("Students", 14, finalY);

        const studentsData = courseData.students
            .filter(s => !selectedYear || s.academicYear === selectedYear)
            .map(s => [
                s.name,
                s.admissionNumber || 'N/A',
                s.campusName,
                `${s.progress}%`,
                s.courseStatus.toUpperCase()
            ]);

        autoTable(doc, {
            startY: finalY + 5,
            head: [['Name', 'Adm No', 'Campus', 'Progress', 'Status']],
            body: studentsData,
            headStyles: { fillColor: [79, 70, 229] },
            styles: { fontSize: 9 }
        });

        // Payments Section
        finalY = doc.lastAutoTable.finalY + 15;
        // Check for page break
        if (finalY > 250) {
            doc.addPage();
            finalY = 20;
        }

        doc.text("Financial Transactions", 14, finalY);

        const paymentsData = (courseData.payments.allTransactions || [])
            .filter(p => !selectedYear || p.academicYear === selectedYear)
            .map(p => [
                new Date(p.date).toLocaleDateString(),
                p.studentName,
                p.unitName,
                p.transactionCode || '-',
                parseFloat(p.amount).toLocaleString(),
                p.status.toUpperCase()
            ]);

        autoTable(doc, {
            startY: finalY + 5,
            head: [['Date', 'Student', 'Unit', 'Code', 'Amount', 'Status']],
            body: paymentsData,
            headStyles: { fillColor: [22, 163, 74] }, // Green for money
            styles: { fontSize: 8 }
        });

        doc.save(`${courseData.course.name.replace(/\s+/g, '_')}_Report${yearSuffix}.pdf`);
    };

    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();
        const yearSuffix = selectedYear ? `_AY_${selectedYear}` : '';

        // Overview Sheet
        const overviewData = [
            { Metric: "Course Name", Value: courseData.course.name },
            { Metric: "Campus", Value: courseData.course.campusName },
            { Metric: "Course ID", Value: courseData.course.id },
            { Metric: "Description", Value: courseData.course.description },
            { Metric: "Total Students", Value: courseData.metrics.totalStudents },
            { Metric: "Total Units", Value: courseData.metrics.totalUnits },
            { Metric: "Average Grade", Value: `${courseData.metrics.averageGrade}%` },
            { Metric: "Attendance Rate", Value: `${courseData.metrics.attendanceRate}%` },
        ];
        const wsOverview = XLSX.utils.json_to_sheet(overviewData);
        XLSX.utils.book_append_sheet(wb, wsOverview, "Overview");

        // Students Sheet
        const students = courseData.students
            .filter(s => !selectedYear || s.academicYear === selectedYear)
            .map(s => ({
                Name: s.name,
                Email: s.email,
                AdmissionNo: s.admissionNumber,
                Campus: s.campusName,
                AcademicYear: s.academicYear,
                Progress: `${s.progress}%`,
                Status: s.courseStatus,
                CompletedUnits: s.completedUnits
            }));
        const wsStudents = XLSX.utils.json_to_sheet(students);
        XLSX.utils.book_append_sheet(wb, wsStudents, "Students");

        // Payments Sheet
        const payments = (courseData.payments.allTransactions || [])
            .filter(p => !selectedYear || p.academicYear === selectedYear)
            .map(p => ({
                Date: new Date(p.date).toLocaleDateString(),
                Student: p.studentName,
                Unit: p.unitName,
                TransactionCode: p.transactionCode,
                Amount: parseFloat(p.amount),
                Status: p.status,
                Method: p.method,
                AcademicYear: p.academicYear
            }));
        const wsPayments = XLSX.utils.json_to_sheet(payments);
        XLSX.utils.book_append_sheet(wb, wsPayments, "Payments");

        XLSX.writeFile(wb, `${courseData.course.name.replace(/\s+/g, '_')}_Report${yearSuffix}.xlsx`);
    };

    const fetchCourseDetails = async () => {
        try {
            setLoading(true);
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
            const token = localStorage.getItem('token');

            const response = await fetch(
                `${API_BASE_URL}/tracking/courses/${courseId}/detailed`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) throw new Error('Failed to fetch course details');

            const data = await response.json();
            setCourseData(data);
        } catch (error) {
            console.error('Error fetching course details:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!courseData) {
        return (
            <div className="p-6">
                <p>Course not found</p>
                <button onClick={() => navigate('/course-tracking')} className="text-blue-600">
                    Back to Course Tracking
                </button>
            </div>
        );
    }

    const tabs = [
        { id: 'overview', label: 'Overview', icon: BookOpen },
        { id: 'students', label: 'Students', icon: Users },
        { id: 'units', label: 'Units', icon: FileText },
        { id: 'payments', label: 'Payments', icon: DollarSign },
        { id: 'performance', label: 'Performance', icon: TrendingUp },
        { id: 'certificates', label: 'Certificates', icon: Award }
    ];

    const academicYears = getAcademicYears();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200">
            {/* Header - Glassmorphism */}
            <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-white/20 shadow-sm">
                <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/course-tracking')}
                                className="p-2 rounded-full hover:bg-slate-100/80 transition-colors text-slate-500 hover:text-slate-700"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-cyan-600 bg-clip-text text-transparent">
                                    {courseData.course.name}
                                </h1>
                                <p className="text-slate-500 text-sm font-medium flex items-center gap-2">
                                    <MapPin className="h-3 w-3" />
                                    {courseData.course.campusName}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="hidden md:block text-right mr-2">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Course ID</p>
                                <p className="text-sm font-bold text-slate-700 font-mono">{courseData.course.id}</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleExportPDF}
                                    className="flex items-center px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 hover:text-slate-900 transition-all shadow-sm hover:shadow"
                                >
                                    <Download className="h-4 w-4 mr-2 text-red-500" />
                                    PDF
                                </button>
                                <button
                                    onClick={handleExportExcel}
                                    className="flex items-center px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 hover:text-slate-900 transition-all shadow-sm hover:shadow"
                                >
                                    <Download className="h-4 w-4 mr-2 text-green-600" />
                                    Excel
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Tabs - Integrated into Header */}
                    <div className="mt-6 flex space-x-1 overflow-x-auto pb-1 scrollbar-hide">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${isActive
                                        ? 'bg-violet-100 text-violet-700 shadow-sm'
                                        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                                        }`}
                                >
                                    <Icon className={`h-4 w-4 mr-2 ${isActive ? 'text-violet-600' : 'text-slate-400'}`} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* Year Selection */}
                {(activeTab === 'students' || activeTab === 'payments') && academicYears.length > 0 && (
                    <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
                        {academicYears.map(year => (
                            <button
                                key={year}
                                onClick={() => setSelectedYear(year)}
                                className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all border ${selectedYear === year
                                    ? 'bg-gradient-to-r from-violet-600 to-cyan-600 text-white border-transparent shadow-md'
                                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                    }`}
                            >
                                AY {year}
                            </button>
                        ))}
                    </div>
                )}

                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        {/* Metrics Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[
                                { label: 'Students', value: courseData.metrics.totalStudents, icon: Users, color: 'text-violet-500', bg: 'bg-violet-50' },
                                { label: 'Units', value: courseData.metrics.totalUnits, icon: FileText, color: 'text-cyan-500', bg: 'bg-cyan-50' },
                                { label: 'Avg Grade', value: `${courseData.metrics.averageGrade}%`, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                                { label: 'Attendance', value: `${courseData.metrics.attendanceRate}%`, icon: UserCheck, color: 'text-amber-500', bg: 'bg-amber-50' }
                            ].map((metric, idx) => (
                                <div key={idx} className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/40 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{metric.label}</p>
                                            <p className="text-3xl font-bold text-slate-800">{metric.value}</p>
                                        </div>
                                        <div className={`h-12 w-12 ${metric.bg} rounded-xl flex items-center justify-center shadow-inner`}>
                                            <metric.icon className={`h-6 w-6 ${metric.color}`} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Course Description */}
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-sm border border-slate-200/60">
                            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                <BookOpen className="h-4 w-4 text-violet-500" />
                                Course Description
                            </h2>
                            <p className="text-slate-600 leading-relaxed">{courseData.course.description || 'No description available for this course.'}</p>
                        </div>
                    </div>
                )}

                {activeTab === 'students' && (
                    <div className="space-y-8">
                        {Object.entries(
                            courseData.students
                                .filter(s => s.academicYear === selectedYear)
                                .reduce((campusGroups, s) => {
                                    const cKey = s.campusName || 'Main Campus';
                                    if (!campusGroups[cKey]) campusGroups[cKey] = [];
                                    campusGroups[cKey].push(s);
                                    return campusGroups;
                                }, {})
                        ).sort().map(([campus, campusStudents]) => (
                            <div key={campus} className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden hover:shadow-md transition-shadow">
                                <div className="px-6 py-4 bg-slate-50/50 border-b border-slate-100 flex justify-between items-center">
                                    <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider flex items-center">
                                        <MapPin className="h-4 w-4 mr-2 text-cyan-600" />
                                        {campus}
                                    </h3>
                                    <span className="px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-bold text-slate-500 shadow-sm">
                                        {campusStudents.length} Students
                                    </span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Student</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Progress</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Units</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {campusStudents.map((student) => (
                                                <tr key={student.id} className="hover:bg-slate-50 transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center">
                                                            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-violet-100 to-cyan-100 flex items-center justify-center text-xs font-bold text-violet-600 mr-3 border border-white shadow-sm">
                                                                {student.name.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div className="text-sm font-semibold text-slate-900">{student.name}</div>
                                                                <div className="text-xs text-slate-500">{student.admissionNumber || 'N/A'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="w-full max-w-[140px]">
                                                            <div className="flex justify-between text-[10px] font-bold text-slate-500 mb-1 uppercase tracking-wide">
                                                                <span>Progress</span>
                                                                <span>{student.progress}%</span>
                                                            </div>
                                                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-gradient-to-r from-violet-500 to-cyan-500 rounded-full transition-all duration-1000"
                                                                    style={{ width: `${student.progress}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex flex-wrap gap-1">
                                                            {(student.units || []).map((unit, idx) => (
                                                                <span key={idx} className={`px-2 py-0.5 text-[10px] font-medium rounded border ${unit.status === 'completed' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' :
                                                                    unit.status === 'active' ? 'bg-blue-50 border-blue-100 text-blue-600' :
                                                                        'bg-slate-50 border-slate-100 text-slate-400'
                                                                    }`}>
                                                                    {unit.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${student.courseStatus === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                                                            student.courseStatus === 'in-progress' ? 'bg-cyan-100 text-cyan-800' :
                                                                'bg-slate-100 text-slate-800'
                                                            }`}>
                                                            {student.courseStatus === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                                                            {student.courseStatus}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'units' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {courseData.units.map((unit) => (
                            <div key={unit.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/60 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
                                <div className="flex justify-between items-start mb-4">
                                    <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider ${unit.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                                        unit.status === 'active' ? 'bg-cyan-50 text-cyan-600' :
                                            'bg-amber-50 text-amber-600'
                                        }`}>
                                        {unit.status}
                                    </span>
                                    <div className="h-8 w-8 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-violet-50 transition-colors">
                                        <FileText className="h-4 w-4 text-slate-400 group-hover:text-violet-500" />
                                    </div>
                                </div>
                                <h3 className="font-bold text-slate-800 mb-2 line-clamp-1">{unit.name}</h3>
                                <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 mb-6 h-10">{unit.description}</p>
                                <div className="pt-4 border-t border-slate-100 flex items-center text-xs font-medium text-slate-500">
                                    <User className="h-3 w-3 mr-2 text-violet-400" />
                                    {unit.lecturerName || 'Not Assigned'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'payments' && (
                    <div className="space-y-8">
                        {/* Financial Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/60 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-10">
                                    <DollarSign className="h-16 w-16 text-slate-900" />
                                </div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Potential Revenue</p>
                                <p className="text-3xl font-bold text-slate-800">KES {courseData.payments.total.toLocaleString()}</p>
                            </div>
                            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 shadow-lg text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-10">
                                    <TrendingUp className="h-16 w-16 text-white" />
                                </div>
                                <p className="text-xs font-bold text-emerald-100 uppercase tracking-wider mb-2">Collected Revenue</p>
                                <p className="text-3xl font-bold">KES {courseData.payments.collected.toLocaleString()}</p>
                            </div>
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/60 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-10">
                                    <Clock className="h-16 w-16 text-amber-500" />
                                </div>
                                <p className="text-xs font-bold text-amber-500 uppercase tracking-wider mb-2">Outstanding</p>
                                <p className="text-3xl font-bold text-amber-600">KES {courseData.payments.pending.toLocaleString()}</p>
                            </div>
                        </div>

                        {/* Transactions Table */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h3 className="font-bold text-slate-700">Recent Transactions</h3>
                                <span className="text-xs font-medium text-slate-400">AY {selectedYear}</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead className="bg-slate-50/50">
                                        <tr>
                                            {['Date', 'Student', 'Unit', 'Code', 'Amount', 'Status'].map(header => (
                                                <th key={header} className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {(courseData.payments.allTransactions || [])
                                            .filter(p => p.academicYear === selectedYear)
                                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                                            .map((payment) => (
                                                <tr key={payment.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="px-6 py-4 text-xs font-medium text-slate-500 font-mono">
                                                        {new Date(payment.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-semibold text-slate-700">
                                                        {payment.studentName}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="text-xs font-medium text-violet-600 bg-violet-50 px-2 py-0.5 rounded border border-violet-100">
                                                            {payment.unitName}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-xs font-mono text-slate-400">
                                                        {payment.transactionCode || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 text-right font-mono text-sm font-bold text-slate-700">
                                                        {parseFloat(payment.amount).toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wide ${payment.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                                                            payment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-red-100 text-red-700'
                                                            }`}>
                                                            {payment.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'performance' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200/60 flex flex-col items-center justify-center text-center">
                            <div className="relative h-40 w-40 mb-6">
                                <svg className="h-full w-full transform -rotate-90" viewBox="0 0 100 100">
                                    <circle className="text-slate-100" strokeWidth="8" stroke="currentColor" fill="transparent" r="40" cx="50" cy="50" />
                                    <circle
                                        className="text-violet-600 transition-all duration-1000 ease-out"
                                        strokeWidth="8"
                                        strokeDasharray={251.2}
                                        strokeDashoffset={251.2 - (251.2 * courseData.metrics.assignmentCompletionRate) / 100}
                                        strokeLinecap="round"
                                        stroke="currentColor"
                                        fill="transparent"
                                        r="40"
                                        cx="50"
                                        cy="50"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center flex-col">
                                    <span className="text-3xl font-bold text-slate-800">{courseData.metrics.assignmentCompletionRate}%</span>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">Rate</span>
                                </div>
                            </div>
                            <h3 className="text-lg font-bold text-slate-700 mb-2">Completion Rate</h3>
                            <p className="text-sm text-slate-500 max-w-xs">Percentage of students who have completed all required assignments and units.</p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200/60">
                            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-6">Student Activity Status</h3>
                            <div className="space-y-4">
                                <div className="group flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-violet-200 hover:bg-violet-50/50 transition-all">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-white shadow-sm flex items-center justify-center">
                                            <Users className="h-5 w-5 text-violet-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-slate-700">Active Students</p>
                                            <p className="text-xs text-slate-500">Currently enrolled</p>
                                        </div>
                                    </div>
                                    <span className="text-xl font-bold text-slate-800">{courseData.metrics.activeStudents}</span>
                                </div>

                                <div className="group flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-white shadow-sm flex items-center justify-center">
                                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-slate-700">Completed</p>
                                            <p className="text-xs text-slate-500">Finished course</p>
                                        </div>
                                    </div>
                                    <span className="text-xl font-bold text-emerald-600">{courseData.metrics.completedStudents}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'certificates' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
                        <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <h2 className="font-bold text-slate-700 flex items-center gap-2">
                                <Award className="h-5 w-5 text-amber-500" />
                                Certificates Issued
                            </h2>
                            <span className="text-xs font-bold bg-white border border-slate-200 px-3 py-1 rounded-full text-slate-500">
                                Total: {courseData.certificates.length}
                            </span>
                        </div>
                        <div className="divide-y divide-slate-50">
                            {courseData.certificates.map((cert) => (
                                <div key={cert.id} className="p-6 hover:bg-slate-50/50 transition-colors flex items-center justify-between group">
                                    <div className="flex items-center gap-4">
                                        <div className="h-12 w-12 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600">
                                            <Award className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-slate-800">{cert.studentName}</p>
                                            <p className="text-xs text-slate-400 font-mono mt-0.5 uppercase tracking-wider">
                                                {cert.certificateNumber}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex items-center justify-end gap-2 mb-1">
                                            <span className="px-2 py-0.5 text-[10px] font-bold bg-violet-50 text-violet-600 border border-violet-100 rounded uppercase tracking-wide">
                                                Grade: {cert.grade}
                                            </span>
                                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide border ${cert.status === 'issued' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'
                                                }`}>
                                                {cert.status}
                                            </span>
                                        </div>
                                        <p className="text-[10px] font-medium text-slate-400">
                                            Issued: {new Date(cert.issueDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {courseData.certificates.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-16 text-center">
                                    <div className="h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                        <Award className="h-8 w-8 text-slate-300" />
                                    </div>
                                    <h3 className="text-slate-900 font-medium">No certificates yet</h3>
                                    <p className="text-slate-500 text-sm mt-1 max-w-sm">
                                        Certificates will appear here once they are generated for eligible students.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
